import React, { useCallback, useState } from "react";
import { commitMutation, graphql } from "react-relay";

import { faCaretDown, faLock } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import ViewportContext from "~/common/ViewportContext";
import recaptcha from "~/lib/recaptcha";
import { environment } from "~/models";
import { useModals } from "~/ui";
import type { UserAttrs } from "~/ui/legacy/Avatar";
import Avatar from "~/ui/legacy/Avatar";
import Menu from "~/ui/menu";
import toasty from "~/ui/toast";
import Tippy from "~/ui/Tooltip";

import { RemoveAccessModal } from "./RemoveAccessModal";

import type { ShareModalQuery$data } from "~/__generated__/ShareModalQuery.graphql";
import type { StreamInviteRowResendStreamInviteMutation } from "~/__generated__/StreamInviteRowResendStreamInviteMutation.graphql";
import type { StreamInviteRowUninviteMutation } from "~/__generated__/StreamInviteRowUninviteMutation.graphql";

import styles from "../css/ShareModal.module.css";

const resendInviteMutation = graphql`
	mutation StreamInviteRowResendStreamInviteMutation($id: ID!, $captchaToken: String!) {
		noteStreamResendStreamInvite(id: $id, captchaToken: $captchaToken) {
			invite {
				id
				isExpired
			}
		}
	}
`;

const uninviteMutation = graphql`
	mutation StreamInviteRowUninviteMutation($id: ID!, $inviteId: ID!) {
		noteStreamUninvite(id: $id, inviteId: $inviteId) {
			noteStream {
				id
				pendingStreamInvites {
					id
					email
				}
			}
		}
	}
`;

interface StreamInviteRowProps {
	user: UserAttrs;
	viewer: ShareModalQuery$data["viewer"];
	readOnly: boolean;
	streamId: string;
	isExpired: boolean;
}

export default function StreamInviteRow({ user, viewer, readOnly, streamId, isExpired }: StreamInviteRowProps) {
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const modals = useModals();
	const { isMobile } = React.useContext(ViewportContext);

	const resendInvite = useCallback(async () => {
		const captchaToken = await recaptcha("resend_stream_invite");
		commitMutation<StreamInviteRowResendStreamInviteMutation>(environment, {
			mutation: resendInviteMutation,
			variables: {
				id: user.id,
				captchaToken,
			},
			onCompleted: (response, errors) => {
				if (errors) {
					return toasty.error("Unable to send a reminder, please contact support.");
				}
				return toasty.success("Reminder sent!");
			},
		});
	}, [user]);

	const removeUserAccess = useCallback(async () => {
		const result = await modals.open<boolean>(({ close }) => (
			<RemoveAccessModal onClose={close} userName={user.email || ""} isSelf={false} />
		));
		if (!result) return;

		commitMutation<StreamInviteRowUninviteMutation>(environment, {
			mutation: uninviteMutation,
			variables: {
				id: streamId,
				inviteId: user.id,
			},
		});
	}, [modals, user, streamId]);

	return (
		<>
			<div className={styles.userRow}>
				<Avatar user={user} width="24px" className={styles.userRowAvatar} />
				<span className={styles.nameSection}>
					<span className={styles.name}>{user.email}</span>
					<span className={styles.pendingInvite}>
						&nbsp;({isExpired ? "Expired" : <>Pending{isMobile || <>: Invite sent</>}</>})
					</span>
				</span>
				{readOnly || viewer.user.isGuest ? (
					<Tippy
						placement="top"
						content={
							viewer.user.isGuest
								? "You cannot remove users as a Guest to this workspace"
								: "You only have access to some notes and cannot change user permissions"
						}
					>
						<span className={styles.lockedAccess}>
							Can edit <FontAwesomeIcon icon={faLock} className={styles.lockIcon} />
						</span>
					</Tippy>
				) : (
					<Menu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
						<Menu.Trigger>
							<button className={styles.accessMenuTrigger}>
								Can edit
								<FontAwesomeIcon icon={faCaretDown} className={styles.menuTriggerCaret} />
							</button>
						</Menu.Trigger>
						<Menu.Content data-ignore-focus-lock>
							<Menu.RadioGroup value="EDIT" data-ignore-focus-lock>
								<Menu.RadioItem value="EDIT">Can edit</Menu.RadioItem>
							</Menu.RadioGroup>
							<Menu.Separator />
							<Menu.Item onSelect={resendInvite}>Resend invite</Menu.Item>
							<Menu.Item destructive onSelect={removeUserAccess}>
								Remove...
							</Menu.Item>
						</Menu.Content>
					</Menu>
				)}
			</div>
		</>
	);
}
